import React, { useEffect, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  TextField,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import InventoryServices from "../../services/InventoryServices";

const BootstrapActivation = () => {

  // ==========================
  // STATES
  // ==========================

  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);

  const [uploadLoading, setUploadLoading] = useState(false);

  const [exportLoading, setExportLoading] = useState(false);

  const [file, setFile] = useState(null);
  const [bootstrapActivationDate, setBootstrapActivationDate] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [page, setPage] = useState(0);

  const [pageSize, setPageSize] = useState(50);

  const [rowCount, setRowCount] = useState(0);

  const [search, setSearch] = useState("");

  const [status, setStatus] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  // ==========================
  // SHOW SNACKBAR
  // ==========================

  const showSnackbar = (
    message,
    severity = "success"
  ) => {

    setSnackbar({
      open: true,
      message,
      severity,
    });

  };

  // ==========================
  // FILE CHANGE
  // ==========================

  const handleFileChange = (e) => {

    if (!e.target.files.length)
      return;

    setFile(e.target.files[0]);

  };

  // ==========================
  // BOOTSTRAP ACTIVATION DATE
  // ==========================

  const handleBootstrapActivationDateChange = (e) => {
    setBootstrapActivationDate(e.target.value);
  };

  // ==========================
  // LIST API
  // ==========================

  const fetchBootstrapList = async (
    currentPage = page,
    currentSearch = search,
    currentStatus = status
  ) => {

    try {

      setLoading(true);

      const response =
        await InventoryServices.bootstrapList({

          page: currentPage + 1,

          page_size: pageSize,

          search: currentSearch,

          bootstrap_activation_status:
            currentStatus,

        });

      const data = response.data;

      setRows(data.results || []);

      setRowCount(data.count || 0);

    } catch (error) {

      console.log(error);

      showSnackbar(
        "Unable to load records",
        "error"
      );

    } finally {

      setLoading(false);

    }

  };

  // ==========================
  // INITIAL LOAD
  // ==========================

  useEffect(() => {

    fetchBootstrapList();

  }, [page, pageSize]);

  // ==========================
  // SEARCH
  // ==========================

  const handleSearch = () => {

    setPage(0);

    fetchBootstrapList(
      0,
      search,
      status
    );

  };

  // ==========================
  // RESET
  // ==========================

  const handleReset = () => {

    setSearch("");

    setStatus("");

    setPage(0);

    fetchBootstrapList(
      0,
      "",
      ""
    );

  };

  // ==========================
  // EXPORT EXCEL
  // ==========================

  const handleExport = async () => {

    try {

      setExportLoading(true);

      const response =
        await InventoryServices.exportBootstrap({

          search,

          bootstrap_activation_status:
            status,

        });

      const blob = new Blob(
        [response.data],
        {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
      );

      const url =
        window.URL.createObjectURL(blob);

      const link =
        document.createElement("a");

      link.href = url;

      link.download =
        "bootstrap_export.xlsx";

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);

    } catch (error) {

      console.log(error);

      showSnackbar(
        "Export Failed",
        "error"
      );

    } finally {

      setExportLoading(false);

    }

  };

  // ==========================
  // UPLOAD RESULT
  // ==========================

  const handleUpload = async () => {

    if (!file) {

      showSnackbar(
        "Please select file",
        "warning"
      );

      return;

    }
    if (!bootstrapActivationDate) {
      showSnackbar(
        "Please select bootstrap activation date",
        "warning"
      );
      return;
    }

    if (bootstrapActivationDate > today) {
      showSnackbar(
        "Bootstrap activation date cannot be a future date",
        "warning"
      );
      return;
    }

    try {

      setUploadLoading(true);

      const formData =
        new FormData();

      formData.append(
        "excel_file",
        file
      );

      formData.append(
        "bootstrap_activation_date",
        bootstrapActivationDate
      );

      const response =
        await InventoryServices.uploadBootstrapResult(
          formData
        );

      if (
        response.data.errors &&
        response.data.errors.length
      ) {

        showSnackbar(

          `Uploaded with ${response.data.errors.length} error(s).`,

          "warning"

        );

      } else {

        showSnackbar(
          "Upload Successful"
        );

      }

      setFile(null);
      setBootstrapActivationDate("");

      fetchBootstrapList();

    } catch (error) {

      console.log(error);

      showSnackbar(
        error?.response?.data?.error ||
        "Upload Failed",
        "error"
      );

    } finally {

      setUploadLoading(false);

    }

  };

  // ==========================
  // TABLE COLUMNS
  // ==========================

  const columns = [

    {
      field: "id",
      headerName: "ID",
      width: 90,
    },

    {
      field: "uid",
      headerName: "UID",
      width: 160,
    },

    {
      field: "imei",
      headerName: "IMEI",
      width: 170,
    },

    {
      field: "iccid_primary",
      headerName: "ICCID Primary",
      width: 210,
    },

    {
      field: "iccid_secondary",
      headerName: "ICCID Secondary",
      width: 210,
    },

    {
      field: "msisdn_primary",
      headerName: "MSISDN Primary",
      width: 190,
    },

    {
      field: "msisdn_secondary",
      headerName: "MSISDN Secondary",
      width: 190,
    },

    {
      field: "bootstrap_activation_status",
      headerName: "Bootstrap Status",
      width: 220,
    },

    {
      field: "bootstrap_activation_requested_at",
      headerName: "Requested At",
      width: 220,
      renderCell: (params) => {
        const value = params.row.bootstrap_activation_requested_at;

        if (!value) {
          return "-";
        }

        return new Date(value).toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
      },
    },

  ];



  return (
    <Box p={3}>

      {/* =========================
        Upload Result File
    ========================== */}

      <Card
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 3,
        }}
      >
        <CardContent>

          <Typography
            variant="h5"
            fontWeight="bold"
            color="primary"
          >
            Upload activation result file
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            mt={1}
            mb={3}
          >
            Upload the 17-column M2M platform result file.
            Rows for unknown IDs will be reported as errors,
            while valid rows are processed successfully.
          </Typography>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={2}
            alignItems="center"
          >

            <Button
              variant="outlined"
              component="label"
              sx={{
                minWidth: 170,
              }}
            >
              Choose File

              <input
                hidden
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
              />

            </Button>

            <Typography
              variant="body2"
              sx={{
                flexGrow: 1,
              }}
            >
              {file
                ? file.name
                : "No file selected"}
            </Typography>
            <TextField
              label="Bootstrap Activation Date"
              type="date"
              value={bootstrapActivationDate}
              onChange={handleBootstrapActivationDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: today,
              }}
              sx={{
                minWidth: 220,
              }}
            />

            <Button
              variant="contained"
              color="primary"
              disabled={
                uploadLoading ||
                !file ||
                !bootstrapActivationDate
              }
              onClick={handleUpload}
              sx={{
                minWidth: 120,
              }}
            >
              {uploadLoading ? (
                <CircularProgress
                  size={22}
                  color="inherit"
                />
              ) : (
                "Upload"
              )}
            </Button>

          </Stack>

        </CardContent>
      </Card>

      {/* =========================
        Search Card
    ========================== */}

      <Card
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 3,
        }}
      >
        <CardContent>

          <Typography
            variant="h6"
            fontWeight="bold"
            mb={3}
          >
            Search / Filter
          </Typography>

          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={2}
          >

            <TextField
              fullWidth
              placeholder="Search uid / imei / iccid / msisdn / basket..."
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
            />

            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel id="bootstrap-status-label">
                Bootstrap Status
              </InputLabel>

              <Select
                labelId="bootstrap-status-label"
                value={status}
                label="Bootstrap Status"
                displayEmpty
                onChange={(e) => setStatus(e.target.value)}

              >


                <MenuItem value="requested">
                  Requested
                </MenuItem>

                <MenuItem value="bootstrap">
                  Bootstrap Complete
                </MenuItem>

                <MenuItem value="pending">
                  Pending
                </MenuItem>

                <MenuItem value="failed">
                  Failed
                </MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={handleSearch}
              sx={{
                minWidth: 120,
              }}
            >
              Search
            </Button>

            <Button
              variant="outlined"
              onClick={handleReset}
              sx={{
                minWidth: 120,
              }}
            >
              Reset
            </Button>

            <Button
              variant="contained"
              color="success"
              disabled={exportLoading}
              onClick={handleExport}
              sx={{
                minWidth: 170,
              }}
            >
              {exportLoading ? (
                <CircularProgress
                  size={22}
                  color="inherit"
                />
              ) : (
                "Export Excel"
              )}
            </Button>

          </Stack>

        </CardContent>
      </Card>

      {/* =========================
      Bootstrap Requested Table
========================= */}

      <Card
        elevation={2}
        sx={{
          borderRadius: 3,
        }}
      >
        <CardContent>

          <Typography
            variant="h6"
            fontWeight="bold"
            mb={2}
          >
            Bootstrap Requested
          </Typography>

          <Box
            sx={{
              width: "100%",

              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                fontWeight: 600,
              },

              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
            }}
          >

            <DataGrid

              autoHeight

              rows={rows}

              columns={columns}

              getRowId={(row) => row.id}

              loading={loading}

              pagination

              paginationMode="server"

              rowCount={rowCount}

              pageSizeOptions={[25, 50, 100]}

              paginationModel={{
                page,
                pageSize,
              }}

              onPaginationModelChange={(model) => {

                setPage(model.page);

                setPageSize(model.pageSize);

              }}

              disableRowSelectionOnClick

              slots={{

                noRowsOverlay: () => (

                  <Box
                    sx={{
                      height: 250,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "gray",
                      fontSize: 18,
                    }}
                  >
                    No Data Available
                  </Box>

                ),

              }}

            />

          </Box>

        </CardContent>

      </Card>

      {/* =========================
      Snackbar
========================= */}

      <Snackbar

        open={snackbar.open}

        autoHideDuration={3000}

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}

        onClose={() =>

          setSnackbar({
            ...snackbar,
            open: false,
          })

        }

      >

        <Alert

          severity={snackbar.severity}

          variant="filled"

          onClose={() =>

            setSnackbar({
              ...snackbar,
              open: false,
            })

          }

          sx={{
            width: "100%",
          }}

        >

          {snackbar.message}

        </Alert>

      </Snackbar>



    </Box>
  );

};

export default BootstrapActivation;